"use client";

import { useState, useEffect } from "react";
import { createStorefrontApiClient } from "@shopify/storefront-api-client";
import { ShoppingCart, ExternalLink, Minus, Plus } from "lucide-react";
import { useTranslation } from "next-i18next";

import { toast } from "sonner";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { ButtonMaster } from "@/components/Atoms/Buttons";
import { MultiPurposeTitle } from "@/components/Atoms/Title";
import { MultiPurposeParagraph } from "@/components/Atoms/Paragraph";
import {
  mapShopifyCartToGa4,
  trackBeginCheckout,
  trackViewCart,
} from "@/lib/ga4-ecommerce";
import styles from "./index.module.scss";

interface ShopifyMiniCartProps {
  domain: string;
  storefrontAccessToken: string;
  label?: string;
  color?: any;
  cta?: string;
  type?: any;
  customColor?: any;
  config: any;
}

export default function ShopifyMiniCart({
  domain,
  storefrontAccessToken,
  label,
  color,
  cta,
  type,
  customColor,
  config,
}: ShopifyMiniCartProps) {
  const { t } = useTranslation();
  const [cart, setCart] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [cartId, setCartId] = useState<string | null>(null);
  const [cartOpen, setCartOpen] = useState(false);

  // Initialize Shopify client
  const getClient = () => {
    // Make sure domain is properly formatted
    let formattedDomain = domain;
    if (!formattedDomain.startsWith("http")) {
      formattedDomain = `https://${formattedDomain}`;
    }

    try {
      return createStorefrontApiClient({
        storeDomain: formattedDomain,
        apiVersion: "2025-10",
        publicAccessToken: storefrontAccessToken,
      });
    } catch (error) {
      toast.error("Failed to initialize Shopify client");
      return null;
    }
  };

  const pushCartEcommerceEvent = (
    event: "view_cart" | "begin_checkout",
    cartData: NonNullable<typeof cart>,
  ) => {
    if (!cartData?.lines?.edges?.length) return;
    const { cart_id, cart_total_quantity, currency, value, items } =
      mapShopifyCartToGa4(cartData);
    const ecommerce = { currency, value, items };
    const extras = { cart_id, cart_total_quantity };
    if (event === "view_cart") {
      trackViewCart(ecommerce, extras);
    } else {
      trackBeginCheckout(ecommerce, extras);
    }
  };

  // Fetch cart data
  const fetchCart = async (): Promise<typeof cart | null> => {
    setIsLoading(true);
    try {
      const storedCartId = localStorage.getItem("shopifyCartId");
      if (!storedCartId) {
        setIsLoading(false);
        return null;
      }

      setCartId(storedCartId);
      const client = getClient();
      if (!client) {
        throw new Error("Failed to initialize Shopify client");
      }

      const cartQuery = `
        query GetCart($cartId: ID!) {
          cart(id: $cartId) {
            id
            checkoutUrl
            totalQuantity
            cost {
              subtotalAmount {
                amount
                currencyCode
              }
            }
            lines(first: 10) {
              edges {
                node {
                  id
                  quantity
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      availableForSale
                      product {
                        title
                        handle
                      }
                      image {
                        url
                        altText
                      }
                      price {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      const { data, errors } = await client.request(cartQuery, {
        variables: { cartId: storedCartId },
      });

      if (errors) {
        throw new Error(
          `Failed to fetch cart: ${errors.message || JSON.stringify(errors)}`
        );
      }

      if (data?.cart) {
        setCart(data.cart);
        return data.cart;
      }
      return null;
    } catch (error: any) {
      // If cart not found, don't show error toast
      if (!error.message?.includes("Cart not found")) {
        toast.error(error.message || "Failed to load cart information");
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const openCartFromUserAction = async () => {
    const cartData = await fetchCart();
    setCartOpen(true);
    if (cartData) {
      pushCartEcommerceEvent("view_cart", cartData);
    }
  };

  const handleCheckoutClick = (
    e: React.MouseEvent | React.KeyboardEvent,
  ) => {
    e.preventDefault();
    const checkoutUrl = cart?.checkoutUrl;
    if (!checkoutUrl || !cart) return;
    pushCartEcommerceEvent("begin_checkout", cart);
    window.open(checkoutUrl, "_blank", "noopener,noreferrer");
  };

  // Update item quantity in cart
  const updateQuantity = async (lineId: string, quantity: number) => {
    try {
      if (!cartId) {
        return;
      }

      if (quantity <= 0) {
        // If quantity is 0 or less, remove the item
        removeItem(lineId);
        return;
      }

      const client = getClient();
      if (!client) {
        throw new Error("Failed to initialize Shopify client");
      }

      const updateQuantityMutation = `
        mutation UpdateCartLine($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
          cartLinesUpdate(cartId: $cartId, lines: $lines) {
            cart {
              id
              totalQuantity
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      const variables = {
        cartId,
        lines: [
          {
            id: lineId,
            quantity: quantity,
          },
        ],
      };

      const { data, errors } = await client.request(updateQuantityMutation, {
        variables,
      });

      if (errors) {
        const errorMessage = Array.isArray(errors) 
          ? errors[0]?.message 
          : (errors as any)?.message || JSON.stringify(errors);
        throw new Error(`Failed to update item quantity: ${errorMessage}`);
      }

      if (data?.cartLinesUpdate?.userErrors?.length > 0) {
        throw new Error(`Failed to update item quantity: ${data.cartLinesUpdate.userErrors[0]?.message || "Unknown error"}`);
      }

      // Refresh cart data
      fetchCart();
    } catch (error: any) {
      toast.error(error.message || "Failed to update item quantity");
    }
  };

  // Remove item from cart
  const removeItem = async (lineId: string) => {
    try {
      if (!cartId) return;

      const client = getClient();
      if (!client) {
        throw new Error("Failed to initialize Shopify client");
      }

      const removeItemMutation = `
        mutation RemoveItem($cartId: ID!, $lineIds: [ID!]!) {
          cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
            cart {
              id
              totalQuantity
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      const { data, errors } = await client.request(removeItemMutation, {
        variables: {
          cartId,
          lineIds: [lineId],
        },
      });

      if (errors || data?.cartLinesRemove?.userErrors?.length > 0) {
        throw new Error("Failed to remove item from cart");
      }

      // Refresh cart data
      fetchCart();
      toast.success("Item removed from cart");
    } catch (error) {
      toast.error("Failed to remove item from cart");
    }
  };

  // Listen for cart updates
  useEffect(() => {
    fetchCart();

    // Set up event listener for cart updates
    const handleCartUpdate = () => {
      fetchCart();
      setCartOpen(true); // Open cart when items are added
    };

    window.addEventListener("shopify:cart:updated", handleCartUpdate);

    return () => {
      window.removeEventListener("shopify:cart:updated", handleCartUpdate);
    };
  }, [domain, storefrontAccessToken]);

  // Format price
  const formatPrice = (amount: string, currencyCode: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
    }).format(Number.parseFloat(amount));
  };

  const cartItemCount = cart?.totalQuantity || 0;
  const cartSubtotal = cart?.cost?.subtotalAmount
    ? formatPrice(
        cart.cost.subtotalAmount.amount,
        cart.cost.subtotalAmount.currencyCode
      )
    : "$0.00";

  const style = {
    "--highlightColor": config?.highlightColor?.color?.hex,
    "--labelColor": color?.hex,
    "--bgColor": config?.backgroundColor?.color?.hex,
  } as React.CSSProperties;

  return (
    <>
      {cartItemCount > 0 && (
        <Sheet open={cartOpen} onOpenChange={setCartOpen}>
          <SheetTrigger asChild>
            <button
              aria-label={`Open cart with ${cartItemCount} ${cartItemCount === 1 ? 'item' : 'items'}`}
              aria-expanded={cartOpen}
              style={style}
              className={`fixed top-[100px] lg:top-[150px] right-12 z-[1000] p-3 ${styles.button} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent`}
              onClick={() => {
                void openCartFromUserAction();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  void openCartFromUserAction();
                }
              }}
              tabIndex={0}
            >
              <ShoppingCart color="white" className="h-5 w-5" />
              {cartItemCount > 0 && (
                <Badge 
                  className="absolute -top-2 -right-2 px-1 w-5 h-5 flex items-center justify-center bg-black text-white"
                  aria-hidden="true"
                >
                  {cartItemCount}
                </Badge>
              )}
            </button>
          </SheetTrigger>
          <SheetContent
            style={style}
            className={`w-full sm:max-w-md flex flex-col ${styles.sheet}`}
            onKeyDown={(e) => {
              // Escape key closes the cart
              if (e.key === "Escape") {
                setCartOpen(false);
              }
            }}
            aria-label="Shopping cart"
          >
            <SheetHeader>
              <SheetTitle>
                <MultiPurposeTitle
                  data={"Your Cart"}
                  color={config?.titleColor?.color}
                  tag="p"
                  type="headline"
                />
              </SheetTitle>
              <SheetDescription asChild>
                <div>
                  <MultiPurposeParagraph
                    data={
                      cartItemCount === 0
                        ? ""
                        : `${cartItemCount} items in your cart`
                    }
                    color={config?.textColor?.color}
                    type="image"
                  />
                </div>
              </SheetDescription>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto py-4">
              {isLoading ? (
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <Skeleton className="h-16 w-16" />
                    <div className="space-y-2 flex-1">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <Skeleton className="h-16 w-16" />
                    <div className="space-y-2 flex-1">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  </div>
                </div>
              ) : cartItemCount === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-center">
                  <ShoppingCart className="h-12 w-12 text-muted-foreground mb-2" />

                  <MultiPurposeParagraph
                    data={"Your cart is empty"}
                    color={config?.textColor?.color}
                    type="image"
                  />
                  <SheetClose asChild>
                    <ButtonMaster
                      anim={"slideLeft"}
                      type={type}
                      color={cta}
                      cta={color}
                      customColor={customColor}
                    >
                      Continue Shopping
                    </ButtonMaster>
                  </SheetClose>
                </div>
              ) : (
                <div className="space-y-4" role="list" aria-label="Cart items">
                  {cart?.lines?.edges.map(({ node }: any, index: number) => {
                    const isOutOfStock = node.merchandise?.availableForSale === false;
                    
                    return (
                    <Card 
                      key={node.id || `cart-item-${index}`} 
                      className={`overflow-hidden ${isOutOfStock ? 'opacity-50' : 'focus-within:ring-2 focus-within:ring-offset-2 focus-within:outline-none'}`}
                      role="listitem"
                      tabIndex={isOutOfStock ? -1 : 0}
                      aria-label={`Cart item ${index + 1}: ${node.merchandise?.product?.title || "Unknown"}, quantity ${node.quantity}${isOutOfStock ? `, ${t("outOfStock").toLowerCase()}` : ''}`}
                      aria-disabled={isOutOfStock}
                    >
                      <CardContent className="p-3">
                        <div className="flex gap-3">
                          <div className="flex flex-col gap-2">
                            <div className="h-20 w-20 bg-white overflow-hidden flex-shrink-0">
                              {node.merchandise.image ? (
                                // eslint-disable-next-line @next/next/no-img-element
                                <img
                                  src={
                                    node.merchandise.image.url ||
                                    "/placeholder.svg"
                                  }
                                  alt={
                                    node.merchandise.image.altText ||
                                    node.merchandise.product.title
                                  }
                                  className="h-full w-full object-contain"
                                />
                              ) : (
                                <div className="h-full w-full bg-muted flex items-center justify-center">
                                  <ShoppingCart className="h-6 w-6 text-muted-foreground" />
                                </div>
                              )}
                            </div>
                            {/* Quantity Controls below image - only show if in stock */}
                            {!isOutOfStock && (
                              <div 
                                className="flex items-center gap-2 border focus-within:ring-2 focus-within:ring-offset-2 focus-within:outline-none" 
                                style={{ borderColor: config?.highlightColor?.color?.hex }}
                                role="group"
                                aria-label={`Quantity for ${node.merchandise?.product?.title || "Unknown"}`}
                              >
                                <button
                                  onClick={() => {
                                    if (!node.id) {
                                      toast.error("Cannot update quantity - missing item ID");
                                      return;
                                    }
                                    updateQuantity(node.id, node.quantity - 1);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      updateQuantity(node.id, node.quantity - 1);
                                    } else if (e.key === "ArrowLeft") {
                                      e.preventDefault();
                                      updateQuantity(node.id, node.quantity - 1);
                                    }
                                  }}
                                  className="p-1 hover:opacity-70 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-transparent"
                                  style={{ 
                                    color: config?.highlightColor?.color?.hex,
                                    outlineColor: config?.highlightColor?.color?.hex
                                  }}
                                  aria-label={`Decrease quantity for ${node.merchandise.product.title}, current quantity ${node.quantity}`}
                                  tabIndex={0}
                                >
                                  <Minus className="h-4 w-4" />
                                </button>
                                <div
                                  className="min-w-[2rem] text-center"
                                  role="status"
                                  aria-live="polite"
                                  tabIndex={-1}
                                >
                                  <MultiPurposeParagraph
                                    data={node.quantity.toString()}
                                    color={config?.highlightColor?.color}
                                    type="image"
                                  />
                                </div>
                                <button
                                  onClick={() => {
                                    if (!node.id) {
                                      toast.error("Cannot update quantity - missing item ID");
                                      return;
                                    }
                                    updateQuantity(node.id, node.quantity + 1);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      updateQuantity(node.id, node.quantity + 1);
                                    } else if (e.key === "ArrowRight") {
                                      e.preventDefault();
                                      updateQuantity(node.id, node.quantity + 1);
                                    }
                                  }}
                                  className="p-1 hover:opacity-70 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-transparent"
                                  style={{ 
                                    color: config?.highlightColor?.color?.hex,
                                    outlineColor: config?.highlightColor?.color?.hex
                                  }}
                                  aria-label={`Increase quantity for ${node.merchandise.product.title}, current quantity ${node.quantity}`}
                                  tabIndex={0}
                                >
                                  <Plus className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                            {/* Out of Stock warning - show below image if out of stock */}
                            {isOutOfStock && (
                              <div className="text-center py-1">
                                <MultiPurposeParagraph
                                  data={t("outOfStock")}
                                  color={config?.textColor?.color}
                                  type="image"
                                  className="text-xs"
                                />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <MultiPurposeParagraph
                              weight={"bold"}
                              data={node.merchandise.product.title}
                              color={config?.textColor?.color}
                              type="label"
                            />
                            {node.merchandise.title !== "Default Title" && (
                              <MultiPurposeParagraph
                                data={node.merchandise.title}
                                color={config?.textColor?.color}
                                type="image"
                              />
                            )}
                            <div className="flex justify-between items-start  gap-2 flex-col">
                              <div>
                                {" "}
                                {/* Wrapper for price info */}
                                {node.merchandise?.price?.amount ? (
                                  <>
                                    <div className="flex items-center gap-2">
                                      <MultiPurposeParagraph
                                        data={formatPrice(
                                          node.merchandise.price.amount,
                                          node.merchandise.price.currencyCode
                                        )}
                                        color={config?.highlightColor?.color}
                                        type="image"
                                      />
                                    </div>
                                    <MultiPurposeParagraph
                                      data={`Total: ${formatPrice(
                                        String(
                                          Number.parseFloat(
                                            node.merchandise.price.amount
                                          ) * node.quantity
                                        ),
                                        node.merchandise.price.currencyCode
                                      )}`}
                                      color={config?.highlightColor?.color}
                                      type="image"
                                      className="text-xs mt-1" // Added styling for total
                                    />
                                  </>
                                ) : (
                                  <div className="text-red-500 text-xs">
                                    ⚠️ Price data missing - Check console for details
                                  </div>
                                )}
                              </div>
                              <div className="flex w-full justify-end">
                                <ButtonMaster
                                  anim={"slideLeft"}
                                  type={type}
                                  color={config?.highlightColor?.color}
                                  cta={config?.highlightColor}
                                  customColor={customColor}
                                  onClick={() => removeItem(node.id)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      removeItem(node.id);
                                    } else if (e.key === "Delete" || e.key === "Backspace") {
                                      e.preventDefault();
                                      removeItem(node.id);
                                    }
                                  }}
                                  aria-label={`Remove ${node.merchandise.product.title} from cart`}
                                  tabIndex={0}
                                >
                                  Remove
                                  <Minus className="ml-1 h-4 w-4" />
                                </ButtonMaster>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    );
                  })}
                </div>
              )}
            </div>

            {cartItemCount > 0 && (
              <SheetFooter
                style={style}
                className={`border-t pt-4 ${styles.sheetFooter}`}
              >
                <div className="w-full space-y-4">
                  <div className="flex justify-between">
                    <MultiPurposeTitle
                      data={"Subtotal"}
                      color={config?.textColor?.color}
                      tag="p"
                      type="headline"
                    />
                    <MultiPurposeTitle
                      data={cartSubtotal}
                      color={config?.textColor?.color}
                      tag="p"
                      type="headline"
                    />
                  </div>

                  <MultiPurposeParagraph
                    data={"Shipping and taxes calculated at checkout"}
                    color={config?.textColor?.color}
                    type="image"
                  />
                  <div className=" gap-2 w-full grid ">
                    <ButtonMaster
                      anim={"slideLeft"}
                      type={type}
                      color={color}
                      cta={cta}
                      customColor={customColor}
                      onKeyDown={handleCheckoutClick}
                      aria-label="Proceed to checkout"
                      tabIndex={0}
                    >
                      <a
                        href={cart?.checkoutUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="focus:outline-none"
                        tabIndex={-1}
                        aria-hidden="true"
                        onClick={handleCheckoutClick}
                      >
                        Checkout <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </ButtonMaster>
                    <SheetClose asChild>
                      <div className="w-full flex ">
                        <ButtonMaster
                          anim={"slideLeft"}
                          type={type}
                          color={color}
                          cta={cta}
                          customColor={customColor}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setCartOpen(false);
                            }
                          }}
                          aria-label="Close cart and continue shopping"
                          tabIndex={0}
                        >
                          Continue Shopping
                        </ButtonMaster>
                      </div>
                    </SheetClose>
                  </div>
                </div>
              </SheetFooter>
            )}
          </SheetContent>
        </Sheet>
      )}
    </>
  );
}
